import './CloudCosts.scss';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { ChartOptions } from 'chart.js';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Line } from 'react-chartjs-2';
import { GTable, IGColumnProps } from '../../../__legacy__/components/table/GTable';
import { getMonth, toLocaleDateString } from '../../../__legacy__/helpers/date.helpers';
import { Home } from '../../../__legacy__/routes/config/Home';
import { S_Card } from '../../../design/card/card/S_Cards';
import { Select } from '../../../design/inputs/select/Select';
import { S_Link } from '../../../design/links/S_Link';
import { S_PaginationProps } from '../../../design/pagination/S_Pagination';
import { IChartData, IHistoryData, IPointData } from '../cloudCosts.store';

const MONTHS = [...Array(12)].map((_, index) => getMonth(new Date(0, index)));

interface IProps {
  historyData: IHistoryData[];
  pagination: S_PaginationProps;
  chartData: IChartData;
  users: { label: string; value: string }[];
  userId: string | null;
  isSelectDisabled: boolean;
  handleChangeUser: (value: string) => Promise<void>;
  sorting: {
    orderBy: string;
    orderType: string;
  };
  workspaceId: string;
  isLoading: boolean;
  onSortingChange: (orderBy: string, orderType: string) => Promise<void>;
}

export const CloudCostsHistory = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  function renderDate(item: IHistoryData): ReactElement {
    return <span>{toLocaleDateString(item.dateCreated)}</span>;
  }

  function renderType(item: IHistoryData): ReactElement {
    return <span>{t(item.jobType)}</span>;
  }

  function renderAmount(item: IHistoryData): ReactElement {
    return <span>{item.costCalculated ? `${Math.ceil(item.costCalculated * 100)}` : t('na')}</span>;
  }

  function renderName(item: IHistoryData): ReactElement {
    const { workspaceId } = props;

    const isTraining = item.jobType === 'Training' || item.jobType === 'AutoTraining';

    if (item.isDeleted) {
      return (
        <div
          className='job-name'
          title={t('job_deleted')}
        >
          <FontAwesomeIcon
            className='job-trash-icon'
            icon={faTrash}
          />
          {item.jobName}
        </div>
      );
    }

    const link = isTraining
      ? Home.Trainings.Details.Metrics.withParams({ workspaceId, jobId: item.id })
      : Home.Inferences.Details.Metrics.withParams({ workspaceId, jobId: item.id });

    return (
      <S_Link
        color='text'
        to={link}
      >
        {item.jobName}
      </S_Link>
    );
  }

  const gColumns: IGColumnProps<IHistoryData>[] = [
    {
      sortBy: 'jobName',
      headerName: t('cloud_costs.name'),
      renderer: renderName,
    },
    {
      sortBy: 'dateCreated',
      headerName: t('cloud_costs.date'),
      renderer: renderDate,
    },
    {
      sortBy: 'jobType',
      headerName: t('cloud_costs.type'),
      renderer: renderType,
    },
    {
      sortBy: 'costCalculated',
      headerName: t('cloud_costs.calculated_cost'),
      renderer: renderAmount,
    },
  ];

  const data = {
    labels: MONTHS,
    datasets: Object.keys(props.chartData).map((key) => ({
      label: key,
      data: props.chartData[key].map((point): IPointData => ({ x: point.x, y: Math.ceil(point.y * 100) })),
      fill: false,
      borderColor: '#4bc0c0',
    })),
  };

  const options: ChartOptions<'line'> = {
    scales: {
      x: {
        min: 0,
        max: 12,
        title: {
          font: { size: 18 },
          display: true,
        },
      },
      y: {
        min: 0,
        title: {
          font: { size: 18 },
          display: true,
          text: t('credits'),
        },
      },
    },
  };

  const userOptions = [...props.users, { value: '', label: t('everyone') }];

  return (
    <S_Card className='cloud-costs-history'>
      <div className='info'>
        <Select
          placeholder={t('cloud_costs.select_user')}
          value={userOptions.find((x) => x.value === props.userId)}
          disabled={props.isSelectDisabled}
          options={userOptions}
          onChange={(_, v) => {
            void props.handleChangeUser(v.value ?? '');
          }}
        />
        <GTable
          isLoading={props.isLoading}
          columns={gColumns}
          items={props.historyData}
          noItemsPlaceholder={t('no_action_have_been_done_yet')}
          paginationProps={props.pagination}
          onSortChanged={props.onSortingChange}
          sortingModel={[props.sorting]}
        />
      </div>
      <div className='info'>
        <Line
          data={data}
          options={{ ...options, maintainAspectRatio: true }}
        />
      </div>
    </S_Card>
  );
});
